import last from 'lodash/last'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import uniqBy from 'lodash/uniqBy'
import { useState, useEffect, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { Link } from 'features/i18n/router'
import {
  groupToGroupContextSchema,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import { GroupMember } from 'lib/_types'
import { useInfinityScrollSimple } from 'hooks'
import { useNavigate } from 'features/i18n/router'
import {
  endpoints,
  useGetMembersQuery,
  useDeputizeMutation,
} from 'store/api/yay/group'
import { useCurrentUser } from 'store/api/yay/user'

import Loading from 'components/Loading'
import Avatar from 'components/Avatar'
import Nickname from 'components/Nickname'
import LoadMore from 'components/LoadMore'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'

let searchTO = null

const GroupModeratorsModal = (props: {
  groupId: string
  onClose: Function
}) => {
  const { groupId, onClose } = props

  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()

  const currentUserId = useCurrentUser()?.data?.id
  const [__checkIds, __checkIdsSet] = useState([])
  const [__isActiveConfirmBox, __isActiveConfirmBoxSet] = useState(false)
  const [__search, __searchSet] = useState<string>('')
  const [__fromId, __fromIdSet] = useState<number>(null)
  const [__groupMembers, __groupMembersSet] = useState<Array<GroupMember>>([])
  const [__groupMembersSearch, __groupMembersSearchSet] = useState<
    Array<GroupMember>
  >([])

  const [deputize, deputizeRes] = useDeputizeMutation()

  const groupRes = endpoints?.getGroup?.useQueryState?.({
    groupId: groupId?.toString?.(),
  })

  const isSearching = __search?.length > 0

  const groupMembersRes = useGetMembersQuery({
    groupId,
    ...(!isSearching && { fromId: __fromId }),
    keyword: __search,
  })

  const loadMore = () => {
    __fromIdSet(last(groupMembersRes?.data)?.user?.id)
  }

  const { loadMoreRef } = useInfinityScrollSimple({
    loadMore,
  })

  useEffect(() => {
    __groupMembersSearchSet([])

    if (isSearching) {
      __groupMembersSearchSet(groupMembersRes?.data ?? [])
    } else {
      __groupMembersSet(
        uniqBy(
          [
            ...__groupMembers?.map?.((x) => {
              return (
                groupMembersRes?.data?.find?.(
                  (y) => y?.user?.id == x?.user?.id
                ) ?? x
              )
            }),
            ...(groupMembersRes?.data ?? []),
          ],
          (x) => x?.user?.id
        )
      )
    }
  }, [groupMembersRes])

  const groupMembersFiltered = (
    isSearching ? __groupMembersSearch : __groupMembers
  )?.filter?.((x) => {
    return (
      !includes(groupRes?.data?.moderator_ids, x?.user?.id) &&
      !x?.pending_deputize &&
      x?.user?.id != groupRes?.data?.owner?.id
    )
  })

  return (
    <Fragment>
      <form
        className="Modal__wrapper"
        onSubmit={(e) => {
          e.preventDefault()

          deputize({
            groupId,
            user_ids: __checkIds,
          }).then((res) => {
            // Some strange TS upon `res?.data`.
            if (res?.['data']?.result == 'success') {
              snowplowEvent.add_moderator({
                group: groupToGroupContextSchema({
                  group: groupRes.data,
                  currentUserId,
                }),
                users: groupMembersFiltered
                  .filter((user) =>
                    __checkIds.includes(user.user.id.toString())
                  )
                  .map((user) =>
                    userToUserContextSchemaData({ user: user.user })
                  ),
              })

              __isActiveConfirmBoxSet(true)
            }
          })
        }}
      >
        <div className="Modal__header">
          <Link
            className="Modal__header__close"
            to={location.pathname}
            onClick={(e) => {
              if (typeof onClose !== 'undefined') {
                e.preventDefault()
                onClose?.()
              }
            }}
          />

          <h2 className="Modal__header__h2">
            {t('group_moderators_modal.group_info_add_moderator')}
          </h2>
        </div>

        <div className="Modal__search">
          <div className="Search">
            <input
              className="Search__input"
              placeholder={t(
                'group_moderators_modal.search_bar_placeholder_invite_users_group'
              )}
              type="text"
              onChange={(e) => {
                window.clearTimeout(searchTO)
                searchTO = window.setTimeout(() => {
                  __searchSet(e?.target?.value)
                }, 400)
              }}
            />
          </div>
        </div>

        <div className="Modal__body">
          {(groupMembersRes?.data?.length ?? 0) == 0 &&
            groupMembersRes?.isLoading && <Loading />}

          {groupMembersRes?.isSuccess &&
            (groupMembersFiltered?.length ?? 0) === 0 && (
              <div className="Placeholder Placeholder--recommend-users">
                <p className="Placeholder__p">
                  {t('group_moderators_modal.group_moderator_empty_view_title')}
                </p>
              </div>
            )}

          {groupMembersFiltered?.length > 0 && (
            <dl className="RecommendUsers">
              {groupMembersFiltered?.map?.((x) => (
                <label
                  className="RecommendUsers__item RecommendUsers__item--group-invite"
                  key={x?.user?.id}
                >
                  <div className="RecommendUsers__item__user">
                    <Avatar user={x?.user} showVipCircle showOnlineStatus />

                    <div className="RecommendUsers__item__info">
                      <h3 className="RecommendUsers__item__h3">
                        <Nickname user={x?.user} />
                      </h3>
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    value={x?.user?.id}
                    onChange={(e) => {
                      __checkIdsSet(
                        filter(
                          e?.target?.form?.elements,
                          ($input: HTMLInputElement) => $input?.checked
                        )?.map?.(($input: HTMLInputElement) => $input?.value)
                      )
                    }}
                  />

                  <span className="RecommendUsers__item__circle" />
                </label>
              ))}

              {!isSearching && (
                <LoadMore
                  isLoading={groupMembersRes?.isFetching}
                  hidden={groupMembersRes?.data?.length == 0}
                  className="RecommendUsers__load-more"
                  onClick={() => {
                    loadMore()
                  }}
                  ref={loadMoreRef}
                />
              )}
            </dl>
          )}
        </div>

        <div className="Modal__footer Modal__footer--minus-margin">
          <button
            className="Button"
            disabled={__checkIds?.length == 0 || deputizeRes?.isLoading}
          >
            {t('group_moderators_modal.submit')}
          </button>
        </div>
      </form>

      <ConfirmBoxBasic
        isActive={__isActiveConfirmBox}
        onClose={() => {
          navigate(location?.pathname)
        }}
        onOk={() => {
          navigate(location?.pathname)
        }}
        title={t('group_moderators_modal.features_call_added_moderator')}
        confirmButtonText={t('group_moderators_modal.submit')}
      />
    </Fragment>
  )
}

export default GroupModeratorsModal
